/**
 * Fez-specific wiring for the generic nodeMorph differ.
 *
 * Provides:
 *  - Fez.nodeMorph(target, src, opts?)    - public: accepts string|Element|DocumentFragment
 */

import { nodeMorph } from './morph.js';
import { runTransition } from './transitions.js';

/**
 * Content signature for component identity and rewrite checks.
 *
 * Live components hash their mount-time source snapshot (_fezSignature, the
 * original outerHTML captured in connect.js before the custom tag is replaced).
 * New placeholders hash their own outerHTML. Both sides go through the same
 * parse -> serialize pipeline, so identical source bytes hash equal.
 *
 * Unkeyed identity is strict: tag + attributes + innerHTML must all match,
 * otherwise the component is destroyed and recreated with a fresh init().
 *
 * Keyed identity (fez-key / key / id) still preserves by default for props
 * refresh, but if the source signature changed (slot text, static children),
 * shouldPreserve returns false and the component is rewritten.
 */
function hashText(text) {
  text = String(text || '').trim();
  let hash = 2166136261;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function signatureHash(node) {
  if (node._fezSigHash) {
    return node._fezSigHash;
  }
  const text = String(node?._fezSignature ?? node?.outerHTML ?? '').trim();
  const result = hashText(text);
  node._fezSigHash = result;
  return result;
}

/**
 * Inner HTML of a serialized source tag (between first `>` and last `</`).
 * Used so attribute/prop-only changes still preserve + refresh, while slot
 * text changes force a rewrite.
 */
function innerFromOuterHtml(html) {
  const src = String(html || '');
  const start = src.indexOf('>');
  const end = src.lastIndexOf('<');
  if (start < 0 || end <= start) {
    return '';
  }
  return src.slice(start + 1, end);
}

/**
 * True when a live component may stay mounted against a new template placeholder.
 * Slot / light-DOM content is compared via the mount-time source snapshot
 * (_fezSignature) vs the new placeholder. If children changed
 * (e.g. <ui-label>PDF</ui-label> → STEP), return false so the component is
 * rewritten even when loop fez-key still matches the same index.
 * Attribute-only changes still preserve and go through props refresh.
 */
function shouldPreserveFezComponent(oldNode, newNode) {
  if (!oldNode?.fez || oldNode.fez._destroyed) {
    return true;
  }
  if (oldNode._fezSignature == null) {
    return true;
  }
  if (!newNode || newNode.nodeType !== 1) {
    return true;
  }
  return hashText(innerFromOuterHtml(oldNode._fezSignature)) === hashText(newNode.innerHTML);
}

/**
 * Explicit identity key: compiler-promoted _fezKey property, or a user-authored
 * fez-key= attribute (server HTML never passes through the template compiler,
 * so the attribute form is the pjax-path opt-in for morph semantics).
 */
function explicitFezKey(node) {
  return node._fezKey ?? node.getAttribute?.('fez-key') ?? undefined;
}

// Component name from the `<name>-fez` wrapper class (connect.js)
function wrapperName(node) {
  for (const cls of node.classList || []) {
    // component names always contain a dash, so `x-fez` alone is not one
    const name = cls.endsWith('-fez') ? cls.slice(0, -4) : '';
    if (name.includes('-')) {
      return name;
    }
  }
  return null;
}

function fezKeyAlias(internalKey, keyAttr, base, node) {
  if (internalKey !== undefined) {
    return 'key-' + internalKey;
  }
  if (keyAttr) {
    return 'key-' + keyAttr;
  }
  return `${base}:sig-${signatureHash(node)}`;
}

/**
 * Describe a live (old) fez component node so the differ can:
 *  - match by UID
 *  - also match against template placeholders by id and fez-name class
 *  - preserve the subtree (no internal morphing)
 *  - exclude from soft-matching
 */
function fezDescribeOld(node) {
  if (node.nodeType !== 1) {
    return null;
  }
  if (!node.classList?.contains('fez') || !node.fez) {
    return null;
  }

  const aliases = [];
  if (node.id) {
    aliases.push('id-' + node.id);
  }
  // Compiler-injected internal keys disambiguate fez component siblings inside loops.
  // Without this alias, multiple <my-comp> siblings all collapse onto the first
  // old child via the shared 'fez-class-my-comp' alias.
  const internalKey = explicitFezKey(node);
  if (internalKey !== undefined) {
    aliases.push('key-' + internalKey);
  }
  const keyAttr = node.getAttribute?.('key');
  if (keyAttr) {
    aliases.push('key-' + keyAttr);
  }
  const name = node.fez.fezName || wrapperName(node);
  if (name) {
    aliases.push(`fez-class-${name}:sig-${signatureHash(node)}`);
  }
  return {
    key: 'fez-uid-' + node.fez.UID,
    aliases,
    preserve: true,
    softMatch: false,
  };
}

function refreshPreservedComponent(oldNode, newNode) {
  const fez = oldNode.fez;
  if (!fez || fez._destroyed) {
    return;
  }

  // _propsRaw, not fez.props - see fezApplyProps
  let nextProps = fez._propsRaw || {};
  if (newNode && fez.class?.getProps) {
    nextProps = fez.class.getProps(newNode, oldNode);
  }
  fez.fezApplyProps(nextProps);
}

/**
 * Attach Fez.nodeMorph to the Fez root.
 */
export default function attachMorph(Fez) {
  /**
   * Describe a template (new) fez component placeholder so it matches against
   * the live component's `fez-class-` alias. Recognizes three forms:
   *   1. Inline-rendered: <div class="fez my-comp-fez"> (already mounted-ish)
   *   2. Raw custom tag:  <my-comp>                 (server-rendered placeholder)
   *   3. fez= attribute:  <div fez="my-comp">       (server-rendered placeholder)
   */
  function fezDescribeNew(node) {
    if (node.nodeType !== 1) {
      return null;
    }

    // Compiler-injected internal keys disambiguate fez component siblings inside loops.
    // Prefer it over the shared tag-based alias so each new placeholder maps to
    // its own old child instead of all collapsing onto the first one.
    const internalKey = explicitFezKey(node);
    const keyAttr = node.getAttribute?.('key');

    const name = node.classList?.contains('fez') && wrapperName(node);
    if (name) {
      return fezKeyAlias(internalKey, keyAttr, 'fez-class-' + name, node);
    }

    const tag = node.tagName?.toLowerCase();
    if (tag && Fez.index?.[tag]) {
      return fezKeyAlias(internalKey, keyAttr, 'fez-class-' + tag, node);
    }

    const fezAttr = node.getAttribute?.('fez');
    if (fezAttr && Fez.index?.[fezAttr]) {
      const attrKey = node.getAttribute?.('fez-key') ?? undefined;
      return fezKeyAlias(attrKey, keyAttr, 'fez-class-' + fezAttr, node);
    }

    return null;
  }

  const fezMorphOpts = {
    describeOld: fezDescribeOld,
    describeNew: fezDescribeNew,

    // Keyed preserve is only valid when source (attrs + slot content) still matches
    shouldPreserve: shouldPreserveFezComponent,

    // Cleanup destroyed fez components
    beforeRemove: (node) => {
      if (node.classList?.contains('fez') && node.fez) {
        node.fez.fezOnDestroy?.();
      }
    },

    // fez:out - play the outro, detach when done. The node stays in the DOM
    // flagged _fezLeaving so the differ ignores it (see morph.js).
    removeNode: (parent, node) => {
      if (node.nodeType === 1 && node._fezOut && node.isConnected && !node._fezLeaving) {
        node._fezLeaving = true;
        node.style.pointerEvents = 'none';
        // A rejected/failed transition must still detach - otherwise the node
        // stays invisible, pointer-events:none and invisible to the differ.
        runTransition(node, node._fezOut, 'out')
          .then(() => node.remove())
          .catch(() => node.remove());
      } else {
        parent.removeChild(node);
      }
    },

    // Notify preserved fez children that their parent re-rendered
    onPreserve: (oldNode, newNode) => {
      if (oldNode.classList?.contains('fez') && oldNode.fez && !oldNode.fez._destroyed) {
        refreshPreservedComponent(oldNode, newNode);
      }
    },
  };

  /**
   * Public morph helper. Accepts source as a string (HTML) or Element.
   *
   * Tag rules:
   *  - If src root tag matches target's tag, src is used directly.
   *  - Otherwise src is treated as the new children of target (wrapped in a
   *    same-tag container so children parse in the right context).
   *
   * Target's own attributes are NOT diffed; only its children are updated.
   */
  Fez.nodeMorph = (target, src, opts = {}) => {
    if (!target || target.nodeType !== 1) {
      Fez.onError('nodeMorph', 'target must be an Element');
      return;
    }

    const tagName = target.tagName;
    const tagLower = tagName.toLowerCase();
    let newNode;

    if (typeof src === 'string') {
      src = src.trim();
      const wrapper = document.createElement(tagLower);
      wrapper.innerHTML = src;
      // If the user provided a single root with the matching tag, unwrap it.
      if (
        wrapper.children.length === 1 &&
        wrapper.firstElementChild.tagName === tagName &&
        Array.from(wrapper.childNodes).every(
          (node) => node.nodeType !== 3 || !node.textContent.trim(),
        )
      ) {
        newNode = wrapper.firstElementChild;
      } else {
        newNode = wrapper;
      }
    } else if (src && src.nodeType === 11) {
      // DocumentFragment
      newNode = document.createElement(tagLower);
      newNode.appendChild(src);
    } else if (src && src.nodeType === 1) {
      if (src.tagName === tagName) {
        newNode = src;
      } else {
        newNode = document.createElement(tagLower);
        newNode.appendChild(src);
      }
    } else {
      Fez.onError('nodeMorph', 'src must be a string, Element, or DocumentFragment');
      return;
    }

    nodeMorph(target, newNode, { ...fezMorphOpts, ...opts });
  };
}
